/**
 * SPEC-UnifiedQADetail — header-mounted view-mode toggle.
 *
 * Visible only when `useViewMode().canToggle === true` (senior + admin).
 * Agents see nothing (they are pinned to agent view by `useViewMode`).
 *
 * Two segmented buttons: "Senior" / "Agent". Active state mirrors the
 * current `viewMode`. Click → `setViewMode(next)`. The hook handles
 * localStorage + cross-tab sync; this component is presentational.
 *
 * Branchable logic (`canToggle`, default-by-role, agent pin, storage
 * coercion) is unit-tested in `lib/__tests__/view-mode-helpers.test.ts`.
 * Render-level assertions are covered by the Phase 2 v4 smoke test per
 * the repo's existing test convention (see `vitest.server.config.ts`
 * header).
 */

import { useViewMode, type ViewMode } from "@/hooks/use-view-mode";
import { Starling } from "@/lib/starling-brand";

const OPTIONS: ReadonlyArray<{ value: ViewMode; label: string; testId: string }> = [
  { value: "senior", label: "Senior", testId: "button-view-mode-senior" },
  { value: "agent", label: "Agent", testId: "button-view-mode-agent" },
];

export function ViewModeToggle() {
  const { viewMode, setViewMode, canToggle } = useViewMode();
  if (!canToggle) return null;

  return (
    <div
      data-testid="view-mode-toggle"
      role="group"
      aria-label="View mode"
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "rgba(89,47,150,.06)",
        border: `1px solid ${Starling.purple}33`,
        borderRadius: 6,
        padding: 2,
        gap: 2,
      }}
    >
      {OPTIONS.map((opt) => {
        const active = viewMode === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setViewMode(opt.value)}
            data-testid={opt.testId}
            data-active={active ? "true" : "false"}
            aria-pressed={active}
            style={{
              fontFamily: Starling.fontHead,
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 9px",
              borderRadius: 4,
              border: "none",
              cursor: "pointer",
              background: active ? Starling.purple : "transparent",
              color: active ? "#FFFFFF" : Starling.purple,
              letterSpacing: "0.02em",
              lineHeight: 1.2,
              transition: "background 120ms ease, color 120ms ease",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default ViewModeToggle;
